import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@tanstack/react-form';
import { X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { SignalVisibilityType } from '@momentum/types';
import { Button } from '@/shared/components/ui/Button';
import { useAppForm } from '@/shared/components/form/form';
import { SeverityField } from '../create-signal/SeverityField';
import { VisibilityToggle } from '../create-signal/VisibilityToggle';
import { VisibleToField } from '../create-signal/VisibleToField';
import { AnonymousToggle } from '../create-signal/AnonymousToggle';
import { ApplicabilityScopeField } from '../create-signal/ApplicabilityScopeField';
import type { ReviewableSignal } from './types';

interface IndividualStepProps {
  signal: ReviewableSignal;
  totalCount: number;
  remainingCount: number;
  fromSummary: boolean;
  onSave: (updated: ReviewableSignal) => void;
  onSkip: () => void;
  onAcceptRemaining: () => void;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  isLast: boolean;
}

export const IndividualStep = ({
  signal,
  totalCount,
  remainingCount,
  fromSummary,
  onSave,
  onSkip,
  onAcceptRemaining,
  onBack,
  onNext,
  onCancel,
  isLast,
}: IndividualStepProps) => {
  const { t } = useTranslation('knowledge');

  const form = useAppForm({
    defaultValues: {
      title: signal.title,
      severity: signal.severity,
      visibility: signal.visibility,
      isAnonymous: signal.isAnonymous,
      viewableBy: signal.viewableBy ?? { users: [], teams: [] },
      applicabilityScope: signal.applicabilityScope,
    },
  });

  const visibility = useStore(form.store, (state) => state.values.visibility);
  const title = useStore(form.store, (state) => state.values.title);
  const isTitleEmpty = !title.trim();

  // Clear viewableBy when switching to public
  useEffect(() => {
    if (visibility === SignalVisibilityType.PUBLIC) {
      form.setFieldValue('viewableBy', { users: [], teams: [] });
    }
  }, [visibility, form]);

  const buildUpdated = (): ReviewableSignal => {
    const values = form.state.values;
    const hasChanges =
      values.title !== signal.title ||
      values.severity !== signal.severity ||
      values.visibility !== signal.visibility ||
      values.isAnonymous !== signal.isAnonymous ||
      JSON.stringify(values.viewableBy) !== JSON.stringify(signal.viewableBy) ||
      values.applicabilityScope !== signal.applicabilityScope;

    return {
      ...signal,
      ...values,
      edited: hasChanges || signal.edited,
    };
  };

  const handleNext = () => {
    onSave(buildUpdated());
    onNext();
  };

  const handleSkip = () => {
    onSave({ ...buildUpdated(), excluded: true });
    onSkip();
  };

  const handleAcceptRemaining = () => {
    onSave(buildUpdated());
    onAcceptRemaining();
  };

  const handleBack = () => {
    onSave(buildUpdated());
    onBack();
  };

  const currentNumber = signal.index + 1;

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">
          {fromSummary
            ? t('signalReview.editSignal')
            : t('signalReview.reviewSignals')}
        </h2>
        <div className="flex items-center gap-2 me-6">
          {!fromSummary && (
            <div className="flex gap-1">
              {Array.from({ length: totalCount }, (_, dotIndex) => (
                <div
                  key={dotIndex}
                  className={`h-[7px] w-[7px] rounded-full transition-all ${
                    dotIndex < currentNumber - 1
                      ? 'bg-primary'
                      : dotIndex === currentNumber - 1
                      ? 'bg-primary shadow-[0_0_0_3px_rgba(0,115,234,0.18)]'
                      : 'bg-border'
                  }`}
                />
              ))}
            </div>
          )}
          <span className="text-xs text-muted-foreground">
            {t('signalReview.stepCounter', {
              current: currentNumber,
              total: totalCount,
            })}
          </span>
        </div>
      </div>

      {/* Title field */}
      <div>
        <label className="block text-[13px] font-medium mb-1">
          {t('signalReview.signalLabel')}
        </label>
        <form.AppField
          name="title"
          children={(field) => (
            <textarea
              className="w-full rounded-md border border-border bg-background px-2.5 py-[7px] text-[13px] outline-none resize-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10"
              rows={2}
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
            />
          )}
        />
      </div>

      {/* Properties */}
      <div className="flex flex-col">
        <form.AppField
          name="severity"
          children={(field) => (
            <SeverityField
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        />
        <form.AppField
          name="visibility"
          children={(field) => (
            <VisibilityToggle
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        />

        {/* Visible To — only when Private */}
        {visibility === SignalVisibilityType.PRIVATE && (
          <form.AppField
            name="viewableBy"
            children={(field) => (
              <VisibleToField
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          />
        )}

        <form.AppField
          name="isAnonymous"
          children={(field) => (
            <AnonymousToggle
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        />
      </div>

      {/* Applicability scope (managers only) */}
      <form.AppField
        name="applicabilityScope"
        children={(field) => (
          <ApplicabilityScopeField
            value={field.state.value}
            onChange={field.handleChange}
          />
        )}
      />

      {/* Footer */}
      <div className="flex items-center justify-between border-t pt-3 mt-0.5">
        {fromSummary ? (
          <>
            <Button variant="outline" size="sm" onClick={onCancel}>
              {t('signalReview.cancel')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-xs gap-1"
              >
                <X className="h-3.5 w-3.5" />
                {t('signalReview.skipSignal')}
              </Button>
              <Button
                size="sm"
                onClick={handleNext}
                disabled={isTitleEmpty}
                className="gap-1"
              >
                <Check className="h-3.5 w-3.5" />
                {t('signalReview.saveAndBack')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="gap-1"
            >
              <ChevronLeft className="h-3.5 w-3.5 rtl:rotate-180" />
              {t('signalReview.back')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-xs gap-1"
              >
                <X className="h-3.5 w-3.5" />
                {t('signalReview.skipSignal')}
              </Button>
              {!isLast && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleAcceptRemaining}
                  disabled={isTitleEmpty}
                  className="text-xs"
                >
                  {t('signalReview.acceptRemaining', {
                    count: remainingCount,
                  })}
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                disabled={isTitleEmpty}
                className="gap-1"
              >
                {isLast ? t('signalReview.finish') : t('signalReview.next')}
                <ChevronRight className="h-3.5 w-3.5 rtl:rotate-180" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
